/**
 * gen_svg.js — ES Module
 *
 * Génère un SVG à partir d'une phrase :
 *  - Toutes les lettres sont rendues en MAJUSCULES (police nocode.otf)
 *  - Les suites de MAJUSCULES dans la phrase source passent en knockout
 *    (rectangle noir percé, fill-rule evenodd)
 *  - Espacement visuel uniforme calculé depuis les contours réels des glyphes
 *  - Fond transparent
 *
 * Usage navigateur :
 *   import { generateSVG } from './gen_svg.js';
 *   const svg = await generateSVG("tu ME fatigues", fontArrayBuffer);
 *
 * Usage Node.js CLI :
 *   node gen_svg.js "tu ME fatigues"
 *
 * Dépendance : opentype.js  (npm install opentype.js  |  CDN ci-dessous)
 */

// ── Import opentype.js (CDN en browser, package en Node) ─────────────────────
let _opentype;
async function getOpentype() {
  if (_opentype) return _opentype;
  if (typeof window !== 'undefined') {
    const mod = await import('https://cdn.jsdelivr.net/npm/opentype.js@1.3.4/dist/opentype.module.js');
    _opentype = mod.default ?? mod;
  } else {
    const mod = await import('opentype.js');
    _opentype = mod.default ?? mod;
  }
  return _opentype;
}

// ── Cache police parsée (évite opentype.parse() à chaque génération) ─────────
const _fontCache = new WeakMap();

async function getCachedFont(fontBuffer) {
  const opentype = await getOpentype();
  let font = _fontCache.get(fontBuffer);
  if (!font) { font = opentype.parse(fontBuffer); _fontCache.set(fontBuffer, font); }
  return font;
}

// ── Paramètres ────────────────────────────────────────────────────────────────
const SCALE           = 0.1;   // unités police → pixels
const UNIFORM_GAP     = 10;    // gap visuel inter-lettres (px après scale)
const WORD_GAP_FACTOR = 5.0;   // multiplicateur pour les espaces inter-mots
const A_APPROACH_FACTOR = 0.15; // réduction du gap autour de la lettre A
const PADDING_H       = 20;    // marge horizontale du SVG (px)
const PADDING_V       = 2;     // marge verticale du SVG (px)

// ── Utilitaires partagés ──────────────────────────────────────────────────────

/** Normalise une chaîne en nom de fichier (lettres ASCII minuscules uniquement). */
export function normalizeFilename(s) {
  return s
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z]/g, '')
    .toLowerCase();
}

/** Vérifie si un caractère est une lettre avec une casse haute distincte. */
export function isUpperLetter(ch) {
  return ch !== ch.toLowerCase() && ch === ch.toUpperCase();
}

/**
 * Parse le texte en glyphes en détectant les coupures de mots.
 * @returns {{ glyphs: Array, wordBreaks: Set<number> }}
 */
export function parseGlyphs(text, font) {
  const glyphs     = [];
  const wordBreaks = new Set();
  let   pendingWB  = false;

  for (const ch of text) {
    if (ch === ' ') { pendingWB = true; continue; }
    const upper = isUpperLetter(ch);
    const chUp  = ch.toUpperCase();
    const glyph = font.charToGlyph(chUp);
    if (!glyph || glyph.index === 0) continue;
    const bb = glyph.getBoundingBox();
    if (bb.x1 >= bb.x2 || bb.y1 >= bb.y2) continue;
    if (pendingWB && glyphs.length > 0) { wordBreaks.add(glyphs.length); pendingWB = false; }
    glyphs.push({ char: chUp, glyph, bb, upper });
  }

  return { glyphs, wordBreaks };
}

/**
 * Calcule les offsets X en unités police pour chaque glyphe.
 * @param {number} aApproachFactor - réduction du gap autour de A (ex: 0.15)
 * @returns {number[]}
 */
export function computePositionsU(glyphs, wordBreaks, gapLetters, gapWords, aApproachFactor) {
  const aApproach  = gapLetters * aApproachFactor;
  const positionsU = [];
  let xCursor = 0;

  for (let i = 0; i < glyphs.length; i++) {
    const { bb } = glyphs[i];
    if (i > 0) {
      xCursor += wordBreaks.has(i) ? gapWords : gapLetters;
      if (!wordBreaks.has(i) && (glyphs[i].char === 'A' || glyphs[i - 1].char === 'A')) {
        xCursor -= aApproach;
      }
    }
    const offset = xCursor - bb.x1;
    positionsU.push(offset);
    xCursor = bb.x2 + offset;
  }

  return positionsU;
}

/** Détecte les séquences consécutives de majuscules dans les glyphes. */
export function detectKnockoutRuns(glyphs) {
  const runs = [];
  let i = 0;
  while (i < glyphs.length) {
    if (glyphs[i].upper) {
      const start = i;
      while (i < glyphs.length && glyphs[i].upper) i++;
      runs.push([start, i - 1]);
    } else {
      i++;
    }
  }
  return runs;
}

// ── Cœur de génération ───────────────────────────────────────────────────────

/**
 * Génère un SVG à partir d'une phrase et d'un ArrayBuffer de police.
 *
 * @param {string}      text       - Phrase source (MAJUSCULEs = knockout)
 * @param {ArrayBuffer} fontBuffer - Contenu binaire du fichier .otf / .ttf
 * @param {string}      [color]    - Couleur de remplissage (défaut : #000000)
 * @returns {string}               - Contenu SVG complet
 */
export async function generateSVG(text, fontBuffer, color = '#000000') {
  const font = await getCachedFont(fontBuffer);
  const upem = font.unitsPerEm;

  const gapLetters = UNIFORM_GAP / SCALE;
  const gapWords   = gapLetters * WORD_GAP_FACTOR;

  const { glyphs, wordBreaks } = parseGlyphs(text, font);

  if (glyphs.length === 0) {
    throw new Error('Aucun glyphe trouvable dans la phrase.');
  }

  const positionsU   = computePositionsU(glyphs, wordBreaks, gapLetters, gapWords, A_APPROACH_FACTOR);
  const knockoutRuns = detectKnockoutRuns(glyphs);

  // ── Bbox globale ─────────────────────────────────────────────────────────────
  const letterGlyphs = glyphs.filter(g => /\p{L}/u.test(g.char));
  const rawYMin = letterGlyphs.length ? Math.min(...letterGlyphs.map(g => g.bb.y1)) : 0;
  const rawYMax = letterGlyphs.length ? Math.max(...letterGlyphs.map(g => g.bb.y2)) : 0;
  // Bornes du rectangle knockout : ASCII uniquement (exclut accents et cédilles), fallback sur "H"
  const asciiGlyphs = glyphs.filter(g => /^[A-Z]$/.test(g.char));
  const refBb       = font.charToGlyph('H').getBoundingBox();
  const globalYMin  = asciiGlyphs.length ? Math.min(...asciiGlyphs.map(g => g.bb.y1)) : refBb.y1;
  const globalYMax  = asciiGlyphs.length ? Math.max(...asciiGlyphs.map(g => g.bb.y2)) : refBb.y2;
  const firstXMin   = glyphs[0].bb.x1 + positionsU[0];
  const lastXMax    = glyphs[glyphs.length - 1].bb.x2 + positionsU[glyphs.length - 1];

  const leftEdge  = i => glyphs[i].bb.x1 + positionsU[i];
  const rightEdge = i => glyphs[i].bb.x2 + positionsU[i];

  // ── Calculer les rectangles (unités police) ───────────────────────────────────
  const rectsU = knockoutRuns.map(([start, end]) => {
    const lm = leftEdge(start);
    const re = rightEdge(end);

    let gapL, rectL;
    if (start > 0) {
      gapL  = lm - rightEdge(start - 1);
      rectL = rightEdge(start - 1) + gapL / 2;
    } else {
      gapL  = gapWords;
      rectL = lm - gapL / 2;
    }

    let gapR, rectR;
    if (end < glyphs.length - 1) {
      gapR  = leftEdge(end + 1) - re;
      rectR = re + gapR / 2;
    } else {
      gapR  = gapWords;
      rectR = re + gapR / 2;
    }

    // Match vertical padding to the run's left knockout inset width.
    const vPad = lm - rectL;
    return [rectL, rectR, globalYMax + vPad, globalYMin - vPad];
  });

  // ── Dimensions SVG ────────────────────────────────────────────────────────────
  const contentYMin = Math.min(rawYMin, rectsU.length ? Math.min(...rectsU.map(r => r[3])) : rawYMin);
  const contentYMax = Math.max(rawYMax, rectsU.length ? Math.max(...rectsU.map(r => r[2])) : rawYMax);
  const contentXMin = firstXMin;
  const contentXMax = lastXMax;

  const svgWidth  = (contentXMax - contentXMin) * SCALE + 2 * PADDING_H;
  const svgHeight = (contentYMax - contentYMin) * SCALE + 2 * PADDING_V;

  // ── Génération des paths positionnés ─────────────────────────────────────────
  const fontSize = upem * SCALE;

  function getPositionedPath(glyphInfo, offsetX_u) {
    const x = (offsetX_u - contentXMin) * SCALE + PADDING_H;
    const y = contentYMax * SCALE + PADDING_V;
    return glyphInfo.glyph.getPath(x, y, fontSize).toPathData(3);
  }

  const positionedPaths = glyphs.map((g, idx) => getPositionedPath(g, positionsU[idx]));

  // ── Helpers rectangles ────────────────────────────────────────────────────────
  const uToSvgX = x_u => (x_u - contentXMin) * SCALE + PADDING_H;
  const uToSvgY = y_u => (contentYMax - y_u)  * SCALE + PADDING_V;

  function makeRectPath(rectL, rectR, rectT, rectB) {
    const rx = uToSvgX(rectL), ry = uToSvgY(rectT);
    const rw = (rectR - rectL) * SCALE, rh = (rectT - rectB) * SCALE;
    const f  = n => n.toFixed(3);
    return `M ${f(rx)} ${f(ry)} L ${f(rx+rw)} ${f(ry)} L ${f(rx+rw)} ${f(ry+rh)} L ${f(rx)} ${f(ry+rh)} Z`;
  }

  // ── Assemblage SVG ────────────────────────────────────────────────────────────
  const runByStart = new Map(
    knockoutRuns.map(([s, e], ri) => [s, { start: s, end: e, rect: rectsU[ri] }])
  );

  const lines = [];
  const f3 = n => n.toFixed(3);

  lines.push(
    `<svg xmlns="http://www.w3.org/2000/svg" ` +
    `width="${f3(svgWidth)}" height="${f3(svgHeight)}" ` +
    `viewBox="0 0 ${f3(svgWidth)} ${f3(svgHeight)}">`
  );
  lines.push(`  <!-- ${text.replace(/-->/g, '-- >')} — nocode.otf — tracés vectoriels réels -->`);

  let idx = 0;
  while (idx < glyphs.length) {
    if (runByStart.has(idx)) {
      const { start, end, rect } = runByStart.get(idx);
      const rp      = makeRectPath(...rect);
      const koPaths = positionedPaths.slice(start, end + 1).join(' ');
      const label   = glyphs.slice(start, end + 1).map(g => g.char).join('');
      lines.push(`  <!-- Knockout : ${label} -->`);
      lines.push(`  <path fill="${color}" fill-rule="evenodd" d="${rp} ${koPaths}"/>`);
      idx = end + 1;
    } else {
      lines.push(`  <path d="${positionedPaths[idx]}" fill="${color}"/>`);
      idx++;
    }
  }

  lines.push('</svg>');
  return lines.join('\n');
}

/**
 * Parse la police depuis un ArrayBuffer et retourne une instance opentype.Font.
 * @param {ArrayBuffer} fontBuffer
 * @returns {Promise<object>}
 */
export async function parseFont(fontBuffer) {
  return getCachedFont(fontBuffer);
}

// ── CLI Node.js ───────────────────────────────────────────────────────────────
// IIFE async (pas de top-level await) : certains moteurs JS anciens (WebView
// Android obsolète) échouent au parsing d'un module contenant un await de
// premier niveau, même dans une branche jamais exécutée côté navigateur.
if (typeof process !== 'undefined' && process.argv[1]?.endsWith('gen_svg.js')) {
  (async () => {
    const { readFileSync, writeFileSync } = await import('fs');
    const { fileURLToPath }               = await import('url');
    const { resolve, dirname }            = await import('path');

    const text = process.argv[2];
    if (!text) {
      console.error('Usage : node src/gen_svg.js "tu ME fatigues"');
      process.exit(1);
    }

    const __dirname  = dirname(fileURLToPath(import.meta.url));
    const fontPath   = resolve(__dirname, '../assets/fonts/nocode.otf');
    const outputDir  = resolve(__dirname, '../output');

    const fontBuffer = readFileSync(fontPath).buffer;
    const svg        = await generateSVG(text, fontBuffer);
    const outName    = resolve(outputDir, normalizeFilename(text) + '.svg');

    writeFileSync(outName, svg, 'utf-8');

    try {
      const { createGeneration } = await import('./repositories/generationRepository.js');
      await createGeneration({
        inputText: text,
        outputPath: outName,
        color: '#000000',
      });
    } catch (error) {
      console.warn(`Prisma log skipped: ${error.message}`);
    }

    console.log(`✓  ${outName}`);
  })();
}
